import React, { useState, useRef, useCallback } from 'react'
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react'
import { imageUploadService, ImageUploadResult } from '../../services/imageUploadService'

interface ImageUploadFieldProps {
  label: string
  type: 'icon' | 'cover'
  currentImageUrl?: string
  onImageUploaded: (result: ImageUploadResult) => void
  onImageRemoved: () => void
  projectId: string
  disabled?: boolean
  className?: string
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  type,
  currentImageUrl,
  onImageUploaded,
  onImageRemoved,
  projectId,
  disabled = false,
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const dimensions = type === 'icon' 
    ? { width: 'w-32', height: 'h-32', maxSize: '2MB' }
    : { width: 'w-56', height: 'h-32', maxSize: '10MB' }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) {
      setIsDragging(true)
    }
  }, [disabled])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    if (disabled) return

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileUpload(files[0])
    }
  }, [disabled])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileUpload(files[0])
    }
  }, [])

  const handleFileUpload = async (file: File) => {
    let preview: string | null = null
    
    try {
      setError(null)
      setIsUploading(true)
      setUploadProgress(0)

      // Generate and show preview while uploading
      preview = imageUploadService.generatePreviewUrl(file)
      setPreviewUrl(preview)

      // Upload file (includes moderation)
      const result = await imageUploadService.uploadImage(
        file,
        projectId,
        type,
        (progress) => setUploadProgress(progress)
      )

      // Clean up preview and show final image
      imageUploadService.cleanupPreviewUrl(preview)
      setPreviewUrl(null)
      onImageUploaded(result)

      console.log(`✅ ${type} uploaded successfully:`, result)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed'
      setError(errorMessage)
      console.error(`❌ ${type} upload failed:`, err)
      
      // REMOVE preview if moderation failed
      if (preview) {
        imageUploadService.cleanupPreviewUrl(preview)
        setPreviewUrl(null)
      }
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleRemoveImage = async () => {
    if (!currentImageUrl) return

    try {
      await imageUploadService.deleteImage(currentImageUrl)
      onImageRemoved()
      console.log(`✅ ${type} removed successfully`)
    } catch (err) {
      console.error(`❌ Failed to remove ${type}:`, err)
      // Still call onImageRemoved to update UI, even if delete failed
      onImageRemoved()
    }
  }

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const displayImageUrl = previewUrl || currentImageUrl
  const hasImage = !!displayImageUrl

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-900 dark:text-white">
        {label}
        {type === 'cover' && (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">(required to be featured)</span>
        )}
      </label>

      <div
        className={`
          relative rounded-lg transition-all duration-200 cursor-pointer bg-gray-200 dark:bg-gray-800
          ${isDragging && !disabled 
            ? 'border-2 border-dashed border-blue-400 bg-blue-100 dark:bg-blue-900' 
            : hasImage 
              ? 'border-none' 
              : 'border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
          }
          ${disabled ? 'opacity-50' : ''}
          ${dimensions.width} ${dimensions.height}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!hasImage ? openFileDialog : undefined}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {/* Content */}
        {hasImage ? (
          <div className="relative w-full h-full">
            <img
              src={displayImageUrl}
              alt={`Project ${type}`}
              className="w-full h-full object-cover rounded-lg"
            />
            
            {/* Upload progress overlay */}
            {isUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <div className="text-sm font-medium">{uploadProgress}%</div>
                </div>
              </div>
            )}

            {/* Remove button */}
            {!isUploading && !disabled && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveImage()
                }}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full p-4">
            {isUploading ? (
              <div className="text-blue-400">
                <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-2"></div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{uploadProgress}%</div>
              </div>
            ) : (
              <div>
                {type === 'icon' ? (
                  <ImageIcon className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                ) : (
                  <Upload className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center text-red-500 dark:text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}
    </div>
  )
} 